import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { verifyEmail, resendVerificationEmail } from '../../features/auth/authSlice';

const VerifyEmail = () => {
    const { token } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector(state => state.auth);

    // Guard to prevent double verification calls
    const calledRef = useRef(false);

    const [verificationState, setVerificationState] = useState({
        isChecking: !!token,
        isVerified: false,
        error: null
    });

    const [resendState, setResendState] = useState({
        isResending: false,
        isResent: false,
        error: null,
        cooldown: 0
    });

    // Cooldown timer for resend
    useEffect(() => {
        let timer;
        if (resendState.cooldown > 0) {
            timer = setTimeout(() => {
                setResendState(prev => ({
                    ...prev,
                    cooldown: prev.cooldown - 1
                }));
            }, 1000);
        }
        return () => timer && clearTimeout(timer);
    }, [resendState.cooldown]);

    // Verify email token once
    useEffect(() => {
        if (!token || calledRef.current) return;
        calledRef.current = true;

        const verifyTokenAsync = async () => {
            try {
                await dispatch(verifyEmail({ token })).unwrap();
                setVerificationState({
                    isChecking: false,
                    isVerified: true,
                    error: null
                });
                // Redirect to sign-in after success
                setTimeout(() => navigate('/login'), 3000);
            } catch (err) {
                setVerificationState({
                    isChecking: false,
                    isVerified: false,
                    error: err.message || 'Verification failed. Please try again.'
                });
            }
        };

        verifyTokenAsync();
    }, [dispatch, token, navigate]);

    const handleResendVerification = async () => {
        if (resendState.cooldown > 0) return;

        setResendState(prev => ({
            ...prev,
            isResending: true,
            error: null
        }));

        try {
            await dispatch(resendVerificationEmail()).unwrap();
            setResendState({
                isResending: false,
                isResent: true,
                error: null,
                cooldown: 60
            });
        } catch (err) {
            setResendState({
                isResending: false,
                isResent: false,
                error: err.message || 'Failed to resend verification email. Please try again.',
                cooldown: 0
            });
        }
    };

    // Loading state
    if (token && verificationState.isChecking) {
        return (
            <div className="auth-container">
                <div className="auth-form-container">
                    <div className="auth-message-box">
                        <h1>Verifying Your Email</h1>
                        <div className="loading-spinner"></div>
                        <p>Please wait while we verify your email address...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (token && verificationState.isVerified) {
        return (
            <div className="auth-container">
                <div className="auth-form-container">
                    <div className="auth-message-box success">
                        <h1>Email Verified Successfully!</h1>
                        <p>Thank you for verifying your email address. You can now sign in to your account.</p>
                        <p>You will be redirected to the sign in page shortly...</p>
                        <div className="auth-links">
                            <Link to="/login" className="btn btn-primary">Go to Sign In Now</Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (token && verificationState.error) {
        return (
            <div className="auth-container">
                <div className="auth-form-container">
                    <div className="auth-message-box error">
                        <h1>Verification Failed</h1>
                        <p>{verificationState.error}</p>
                        <p>The verification link may have expired or is invalid. Please request a new verification email.</p>
                        <button
                            onClick={handleResendVerification}
                            className="btn btn-primary"
                            disabled={resendState.isResending || resendState.cooldown > 0}
                        >
                            {resendState.isResending
                                ? 'Sending...'
                                : resendState.cooldown > 0
                                    ? `Resend (${resendState.cooldown}s)`
                                    : 'Resend Verification Email'}
                        </button>
                        {resendState.error && <div className="error-text">{resendState.error}</div>}
                        {resendState.isResent && <div className="success-text">A new verification email has been sent. Please check your inbox.</div>}
                    </div>
                </div>
            </div>
        );
    }

    // Default view
    return (
        <div className="auth-container">
            <div className="auth-form-container">
                <div className="auth-message-box">
                    <h1>Verify Your Email</h1>
                    <p>We've sent a verification email to <strong>{user?.email || 'your email address'}</strong>. Please check your inbox and click the verification link to activate your account.</p>
                    <p className="small">If you don't see the email in your inbox, please check your spam folder.</p>
                    <div className="resend-section">
                        <p>Didn't receive the email?</p>
                        <button
                            onClick={handleResendVerification}
                            className="btn btn-outline"
                            disabled={resendState.isResending || resendState.cooldown > 0}
                        >
                            {resendState.isResending
                                ? 'Sending...'
                                : resendState.cooldown > 0
                                    ? `Resend (${resendState.cooldown}s)`
                                    : 'Resend Verification Email'}
                        </button>
                        {resendState.error && <div className="error-text">{resendState.error}</div>}
                        {resendState.isResent && <div className="success-text">A new verification email has been sent. Please check your inbox.</div>}
                    </div>
                    <div className="auth-links">
                        <Link to="/login" className="btn btn-text">Back to Sign In</Link>
                    </div>
                </div>
            </div>
            <div className="auth-info">
                <h2>One Last Step!</h2>
                <p>Email verification helps us ensure the security of your account and enables you to access all features of our application.</p>
                <div className="verification-steps">
                    <div className="step">
                        <div className="step-number">1</div>
                        <p>Check your email inbox</p>
                    </div>
                    <div className="step">
                        <div className="step-number">2</div>
                        <p>Open the verification email</p>
                    </div>
                    <div className="step">
                        <div className="step-number">3</div>
                        <p>Click the verification link</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail;
